import { find, isError, map } from 'lodash-es';
import { v4 as uuid } from 'uuid';

import getWebAppDetails from 'common/utils/web_app_details';

import { getCurrentLng } from '../i18n/i18n';

import type { Logger, UserMeta } from './log.provider';
import { LogOutbox } from './LogOutbox';

export const NATIVE_LOG_SOURCE = 'siteMobile';
export interface CIAppLog {
    timestamp: Date;
    sessionId: string;
    topic?: string | null;
    source: string;
    applicationSource: string;
    level: string;
    message: string[]; // it looks like we can send anything to the BE

    hostname: string;
    location: string;
    browser: object;
    engine: object;
    OSName: string;
    OSVersion: string;
    platform: object;
    language: string;

    userPk?: string | null;
    studyId?: string;
    subjectId?: string;
    siteId?: string;

    appVersion: string | null; // Both web and native-site | current version of the application the user is using (so something like ‘1.6.0’)
    deviceId?: string; // native-site only | serial no . of device
    deviceName?: string; //native-site only | eg. Samsung Note
}

interface LogContext {
    topic?: string;
    studyId?: string;
    siteId?: string;
    subjectId?: string;
}

function convertLogToAPI(e: unknown[], level: string, logMeta: UserMeta, appVersion: string | null): CIAppLog {
    // Callers may pass a context object (studyId, topic, ...) alongside the message
    const contexts = e as LogContext[];
    const escapedErrors = map(e, (err) => JSON.stringify(err, isError(err) ? Object.getOwnPropertyNames(err) : null));

    let topic: string | null = null;
    if (e && e.length > 1 && contexts[1].topic) {
        topic = contexts[1].topic;
    }
    //when studyId is passed as the second parameter along with message
    // `false` (when fewer than two args) is harmless below: `false?.studyId` is undefined
    const sitestudyIdArr = (e?.length > 1 && find(contexts, 'studyId')) as LogContext | undefined;
    const studyId = sitestudyIdArr?.studyId;
    const siteId = sitestudyIdArr?.siteId;

    const subjectIdArr = (e?.length > 1 && find(contexts, 'subjectId')) as LogContext | undefined;
    const subjectId = subjectIdArr?.subjectId;

    const { userPk, sessionId, hostname, location, browser, engine, os, platform } = logMeta;

    const appLog = {
        timestamp: new Date(),
        userPk,
        sessionId: sessionId || uuid(), // TODO: BE is currently requiring sessionId, but don't have a way of identifying for non-auth users when to generate, so just generating something for now
        topic,
        source: 'web',
        applicationSource: 'admin',
        level,
        message: escapedErrors,
        /**
         * this will log (two examples), which can be parsed again:
         * "message": "[\"abc\", {\"studyId\":\"Study.3ee7ec2d-4578-4921-9f14-b52ee64c33bd\",\"message\":\"Study Info tab accessed\"}]",
         * "message": "[{\"stack\":\"Error: xxxx\\n    at _EditStudyDialog (http://localhost:3000/static/js/3.chunk.js:305:90)\\n    at renderWi ....
         */
        hostname,
        location,
        browser,
        engine,
        OSName: os?.['name'],
        OSVersion: os?.['version'],
        platform: { type: platform?.['type'], vendor: platform?.['vendor'] },
        language: getCurrentLng(),
        studyId,
        siteId,
        subjectId,
        appVersion,
    };

    return appLog;
}

export default class CIAppLogger implements Logger {
    private appVersion: string | null = null;
    private readonly outbox = new LogOutbox();

    public initialize(): void {
        this.appVersion = getWebAppDetails().appVersion;
        this.outbox.scheduleSend();
    }

    public log(e: unknown[], logMeta: UserMeta): void {
        this.writeLog(e, 'debug', logMeta);
    }

    public info(e: unknown[], logMeta: UserMeta): void {
        this.writeLog(e, 'info', logMeta);
    }

    public warn(e: unknown[], logMeta: UserMeta): void {
        this.writeLog(e, 'warn', logMeta);
    }

    public error(e: unknown[], logMeta: UserMeta): void {
        this.writeLog(e, 'error', logMeta);
    }

    public flush(timeoutMs: number): Promise<void> {
        return this.outbox.flush(timeoutMs);
    }

    private writeLog(e: unknown[], level: string, logMeta: UserMeta): void {
        this.outbox.enqueue(convertLogToAPI(e, level, logMeta, this.appVersion));
    }
}
